import type { ReactNode } from "react";

import {
  type ColumnDef,
  type SortingState,
  type Table as TableInstance,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

import { EmptyState } from "@/shared/components/data/EmptyState";
import { ErrorState } from "@/shared/components/data/ErrorState";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { cn } from "@/shared/lib/utils";

interface DataTableProps<T> {
  /** Definición de columnas (TanStack). */
  columns: ColumnDef<T, unknown>[];
  /** Datos ya cargados. Puede ser `undefined` mientras carga. */
  data: T[] | undefined;
  /** Estado de carga de la query. */
  loading?: boolean;
  /** Error de la query — muestra ErrorState. */
  error?: unknown;
  /** Callback de reintento (p.ej. `refetch`). */
  onRetry?: () => void;
  /** Props del EmptyState (si hay 0 rows). */
  empty?: {
    title: string;
    description?: ReactNode;
    actions?: ReactNode;
    icon?: ReactNode;
  };
  /** Props para el estado "no results" (filtros activos → sin matches). */
  noResults?: {
    title?: string;
    description?: ReactNode;
    actions?: ReactNode;
  };
  /** `true` cuando hay filtros activos — distingue empty-real de no-results. */
  filtersActive?: boolean;
  /** Sorting controlado opcional. */
  sorting?: SortingState;
  onSortingChange?: (next: SortingState) => void;
  /** Click en una fila entera (navegación a detalle). Si se pasa, cursor + hover. */
  onRowClick?: (row: T) => void;
  /** Key única por fila (default: `row.id` si existe). */
  getRowId?: (row: T, index: number) => string;
  /** Cantidad de rows skeleton al cargar. */
  skeletonRows?: number;
  /** Clases extra para el wrapper. */
  className?: string;
}

/**
 * Tabla de datos premium. Un solo lugar que decide:
 *   - Jerarquía visual (headers mono-micro, cells sans).
 *   - Densidad (padding, border policy).
 *   - Loading (skeleton rows con mismo footprint).
 *   - Empty vs no-results vs error (3 pantallas distintas).
 *   - Sort visual (ícono tri-estado: idle / asc / desc).
 *   - Row interactivity opcional (hover + cursor + keyboard).
 *
 * Cada feature declara su `ColumnDef<T>[]` y la tabla hace el resto.
 */
export function DataTable<T>({
  columns,
  data,
  loading = false,
  error,
  onRetry,
  empty,
  noResults,
  filtersActive = false,
  sorting,
  onSortingChange,
  onRowClick,
  getRowId,
  skeletonRows = 8,
  className,
}: DataTableProps<T>) {
  const table: TableInstance<T> = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: sorting ? { sorting } : undefined,
    onSortingChange:
      onSortingChange && sorting
        ? (updater) => {
            const next = typeof updater === "function" ? updater(sorting) : updater;
            onSortingChange(next);
          }
        : undefined,
    getRowId: getRowId as ((row: T, index: number, parent?: unknown) => string) | undefined,
  });

  const hasRows = (data?.length ?? 0) > 0;

  return (
    <div
      className={cn("overflow-hidden rounded-2xl border bg-card shadow-card-premium", className)}
    >
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="[&_tr]:border-b [&_tr]:border-border/60">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortDir = header.column.getIsSorted();
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "h-11 px-4 font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground",
                        canSort && "cursor-pointer select-none",
                      )}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort ? <SortIcon dir={sortDir} /> : null}
                      </span>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {loading ? (
              <SkeletonRows columns={columns.length} rows={skeletonRows} />
            ) : error ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columns.length} className="p-0">
                  <ErrorState error={error} onRetry={onRetry} />
                </TableCell>
              </TableRow>
            ) : !hasRows ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columns.length} className="p-0">
                  {filtersActive ? (
                    <EmptyState
                      variant="no-results"
                      title={noResults?.title ?? "Sin resultados"}
                      description={
                        noResults?.description ??
                        "Ningún registro coincide con los filtros aplicados."
                      }
                      actions={noResults?.actions}
                    />
                  ) : (
                    <EmptyState
                      title={empty?.title ?? "Aún no hay datos"}
                      description={empty?.description}
                      actions={empty?.actions}
                      icon={empty?.icon}
                    />
                  )}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={cn(
                    "border-b border-border/40 transition-colors last:border-b-0",
                    onRowClick && "cursor-pointer hover:bg-muted/40",
                  )}
                  onClick={
                    onRowClick
                      ? (e) => {
                          // Ignora el click si el target está dentro de un
                          // elemento interactivo (botón, menú, switch, input)
                          // — así cada celda no necesita stopPropagation.
                          if (isInteractiveTarget(e.target)) return;
                          onRowClick(row.original);
                        }
                      : undefined
                  }
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            if (isInteractiveTarget(e.target)) return;
                            e.preventDefault();
                            onRowClick(row.original);
                          }
                        }
                      : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? "button" : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="px-4 py-3 text-[13.5px] text-foreground">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/**
 * Determina si el target del click/keypress corresponde a un elemento
 * interactivo que ya maneja su propio comportamiento. Si lo es, el row
 * click no debe dispararse.
 */
function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      'button, a, input, select, textarea, [role="menuitem"], [role="menu"], [role="switch"], [role="checkbox"], [data-radix-popper-content-wrapper]',
    ),
  );
}

function SortIcon({ dir }: { dir: false | "asc" | "desc" }) {
  const base = "h-3 w-3 shrink-0";
  if (dir === "asc") return <ArrowUp className={cn(base, "text-foreground")} />;
  if (dir === "desc") return <ArrowDown className={cn(base, "text-foreground")} />;
  return <ArrowUpDown className={cn(base, "opacity-40")} />;
}

function SkeletonRows({ columns, rows }: { columns: number; rows: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow
          key={i}
          className="border-b border-border/40 last:border-b-0 hover:bg-transparent"
        >
          {Array.from({ length: columns }).map((__, j) => (
            <TableCell key={j} className="px-4 py-3.5">
              <Skeleton
                className={cn(
                  "h-3.5",
                  // Varía anchos pseudo-aleatoriamente para que no se vea mecánico.
                  j === 0 ? "w-40" : j === columns - 1 ? "w-16" : "w-28",
                )}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}
